import React, { FC, useEffect, useState } from 'react'
import { updateRunningHourReading } from '../../core/pms/_requests'
import { toast } from 'react-toastify'
import type { RunningHourCounterDto, RunningHourReadingDto } from '../../core/pms/_models'

interface Props {
  visible: boolean
  reading: RunningHourReadingDto
  counter: RunningHourCounterDto
  onClose: () => void
  onSuccess: () => void
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2205,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const EditRunningHourReadingModal: FC<Props> = ({
  visible,
  reading,
  counter,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    readingDate: '',
    value: '',
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (visible && reading) {
      setFormData({
        readingDate: reading.readingDate?.split('T')[0] ?? reading.readingDate,
        value: reading.value?.toString() ?? '',
      })
      setErrors({})
    }
  }, [visible, reading])

  if (!visible) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const nextErrors: { [key: string]: string } = {}
    if (!formData.readingDate) {
      nextErrors.readingDate = 'Reading date is required'
    }
    const value = Number(formData.value)
    if (!formData.value || isNaN(value)) {
      nextErrors.value = 'Reading value is required'
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    setSubmitting(true)
    try {
      await updateRunningHourReading(reading.id!, {
        ...reading,
        counterId: counter.id!,
        readingDate: formData.readingDate,
        value: Number(formData.value),
      })
      toast.success('Reading updated successfully', { position: 'top-center' })
      onSuccess()
    } catch (error: any) {
      toast.error('Failed to update reading', { position: 'top-center' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{ width: '520px', maxWidth: '92vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-0'>Edit Reading</h5>
          <button type='button' className='btn-close' onClick={onClose}></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className='flex-grow-1 overflow-auto px-4 py-3'>
            <div className='mb-4'>
              <label className={LABEL}>Counter</label>
              <input
                type='text'
                className='form-control text-dark'
                value={counter.name}
                disabled
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </div>
            <div className='mb-4'>
              <label className={LABEL}>
                Reading Date <span className='text-danger'>*</span>
              </label>
              <input
                type='date'
                className={`form-control text-dark ${errors.readingDate ? 'is-invalid' : ''}`}
                value={formData.readingDate}
                onChange={(e) => {
                  setFormData({ ...formData, readingDate: e.target.value })
                  if (errors.readingDate) setErrors({ ...errors, readingDate: '' })
                }}
              />
              {errors.readingDate && <div className='invalid-feedback'>{errors.readingDate}</div>}
            </div>
            <div className='mb-4'>
              <label className={LABEL}>
                Reading Value <span className='text-danger'>*</span>
              </label>
              <input
                type='number'
                className={`form-control text-dark ${errors.value ? 'is-invalid' : ''}`}
                value={formData.value}
                onChange={(e) => {
                  setFormData({ ...formData, value: e.target.value })
                  if (errors.value) setErrors({ ...errors, value: '' })
                }}
                step='0.01'
              />
              {errors.value && <div className='invalid-feedback'>{errors.value}</div>}
            </div>
          </div>
          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Cancel
            </button>
            <button type='submit' className='btn btn-primary btn-sm' disabled={submitting}>
              {submitting ? 'Updating...' : 'Update Reading'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
